// THE POOL'S R8c4 OPS (client/src/poolwork.js imports them from here; pool.js runs
// them on its workers, in-realm without a Worker). Pure: no three, no DOM.
//
//   forest.shell  the far canopy volume's CPU work (forestvol.js): an FSH1
//                 file decoded (forestshell.js decodeShell), or one shell
//                 group's merged arrays (the drawn tiles + their skirts) in
//                 128 m cell order (cellruns.js cellSort + sortedIndex)
//   glb.soup      a GLBQ building tile (buildings.js): readGlbSoup + the cell
//                 order of its soup (pool-glb.js)
//   canopy.grey   an SHD2 grey WebP tile (canopygen.js, gs-canopygen-slice):
//                 the decode, the canvas read and the R pick — the main
//                 thread's former decodeGrey, the same calls (createImageBitmap
//                 + OffscreenCanvas, both in a worker): the same bytes
// Each returns typed arrays and transfers their buffers back; the main thread
// only wraps them in three objects.
import { decodeShell } from './forestshell.js';
import { cellSort, sortedIndex } from './cellruns.js';
import { SHELL_LEVELS, SKIRT_DROP_M, TOP_PAD_M, NB, borderEdges } from './forestmarch.js';
export { glbSoup } from './pool-glb.js';

// a group's arrays, the order forestvol.js has always built them in: parts =
// [{ d (a decoded FSH1 tile), sides (its skirt sides, NB) }] in the drawn
// map's order -> { pos (Float32 x / y / z), info (Float32 flag + 4 × level
// index), idx (Uint16 / Uint32 when nv > 65535), ni, ns (skirt sides) }.
// Skirts: each lid border edge a → b down to y0 − SKIRT_DROP_M; the bake's
// wall winding (aTop, bFoot, aFoot) faces (dz, −dx) in x / z — a and b swap
// where that points away from the finer side NB[side]
export function shellArrays(parts) {
  let nv = 0, ni = 0, ns = 0;
  const edges = parts.map(({ d, sides }) => (sides.length ? d.edges || borderEdges(d) : null));
  parts.forEach(({ d, sides }, p) => {
    let ne = 0;
    for (const s of sides) ne += edges[p][s].length;
    nv += d.x.length + 4 * ne; ni += d.index.length + 6 * ne; ns += sides.length;
  });
  const pos = new Float32Array(3 * nv), info = new Float32Array(nv);
  const idx = nv > 65535 ? new Uint32Array(ni) : new Uint16Array(ni);
  let v = 0, o = 0;
  const put = (x, y, z, f) => { pos[3 * v] = x; pos[3 * v + 1] = y; pos[3 * v + 2] = z; info[v] = f; v++; };
  parts.forEach(({ d, sides }, p) => {
    const lv = 4 * SHELL_LEVELS.indexOf(d.cellM), base = v, E = edges[p];
    for (let k = 0; k < d.x.length; k++) put(d.x[k], d.y[k], d.z[k], d.flag[k] + lv);
    for (let k = 0; k < d.index.length; k++) idx[o++] = base + d.index[k];
    const yf = d.y0 - SKIRT_DROP_M;
    for (const s of sides) {
      const [sx, sz] = NB[s];
      for (const [a0, b0] of E[s]) {
        const flip = (d.z[b0] - d.z[a0]) * sx - (d.x[b0] - d.x[a0]) * sz < 0;
        const a = flip ? b0 : a0, b = flip ? a0 : b0, t0 = v;
        put(d.x[a], d.y[a], d.z[a], 1 + lv); put(d.x[b], d.y[b], d.z[b], 1 + lv);
        put(d.x[a], yf, d.z[a], 2 + lv); put(d.x[b], yf, d.z[b], 2 + lv);
        idx[o++] = t0; idx[o++] = t0 + 3; idx[o++] = t0 + 2;
        idx[o++] = t0; idx[o++] = t0 + 1; idx[o++] = t0 + 3;
      }
    }
  });
  return { pos, info, idx, ni, ns };
}

// the group's P3 cell order (APP_VISIBILITY §3e): a cell's box top carries
// the VS lid pad (forestvol.js sorts the same way when it builds here)
export const SHELL_SORT = { padY: TOP_PAD_M + 0.1 };

// args { buf } (the FSH1 bytes, transferred) -> the decoded tile (decodeShell's
// object, its arrays transferred); args { parts: [{ d: { x, y, z, flag,
// index, y0, cellM, i, j, tileM, ntTop }, sides }] } -> { pos, info, idx (in
// cell order), cells: { cells, blocks } | null, ni, ns }
export function forestShell(args) {
  if (args.buf) {
    const d = decodeShell(args.buf);
    return { result: d, transfer: [d.x.buffer, d.y.buffer, d.z.buffer, d.flag.buffer, d.index.buffer] };
  }
  const a = shellArrays(args.parts);
  const cs = cellSort(a.pos, a.idx, SHELL_SORT);
  const idx = cs ? sortedIndex(cs.order, a.idx) : a.idx;
  const result = { pos: a.pos, info: a.info, idx, cells: cs ? { cells: cs.cells, blocks: cs.blocks } : null, ni: a.ni, ns: a.ns };
  return { result, transfer: [a.pos.buffer, a.info.buffer, idx.buffer] };
}

// args { buf (the WebP bytes, copied: the caller's job inputs stay whole), tp (tile_px) }
// -> Uint8Array tp² (the R channel, row-major); a tile not tp² throws (the prep fails, as before)
export async function canopyGrey({ buf, tp }) {
  const bm = await createImageBitmap(new Blob([buf], { type: 'image/webp' }), { colorSpaceConversion: 'none', premultiplyAlpha: 'none' });
  if (bm.width !== tp || bm.height !== tp) { bm.close(); throw new Error(`canopy: SHD2 tile ${bm.width}×${bm.height}, not ${tp}²`); }
  const cx = new OffscreenCanvas(tp, tp).getContext('2d', { willReadFrequently: true });
  cx.drawImage(bm, 0, 0); bm.close();
  const d = cx.getImageData(0, 0, tp, tp).data, a = new Uint8Array(tp * tp);
  for (let k = 0; k < a.length; k++) a[k] = d[4 * k];
  return { result: a, transfer: [a.buffer] };
}
